import { toastr } from 'react-redux-toastr';
import {
    pendingTask, // The action key for modifying loading state
    begin, // The action value if a "long" running task begun
    end, // The action value if a "long" running task ended
} from 'react-redux-spinner';
import { UPDATE_ADMIN_APP_DATA } from './appData.actions';
import { LOADING_START, LOADING_END } from '../../../store/loading';
import * as api from '../../services/api-endpoints';
import { getSymbolicPeriodList } from '../../../services/api-endpoints';
import { formatResponse } from '../../../utils';
import { RuleSetType } from '../../../Enums';


/**
 * @param {ApiAdmin.IHyperfindQuery[]} hyperfindQueries 
 * @returns {AdminAppData.IHyperfind[]}
 */
const parseHyperfindQueries = (hyperfindQueries) => {
    let newHyperfindQueries = [];
    for (let query of hyperfindQueries) {
        newHyperfindQueries.push({
            value: query.id,
            label: query.name,
            desc: query.description,
            visibility: query.visibility
        })
    }
    return newHyperfindQueries;
}

export const fetchRulesetAppData = () => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });
    try {
        const [
            ruleSetsResponse,
            dataviewListResponse,
            hyperfindsResponse,
            customFormatsResponse,
            customDataFieldsResponse,
            symbolicPeriodsListResponse,
            paycodeList,
        ] = await Promise.all([
            api.getAllRuleSet(),
            api.GetDataviewsList(),
            api.getHyperFindQueries(),
            api.getAllCustomFormat(),
            api.getAllCustomDataFields(),
            getSymbolicPeriodList(),
            api.GetPaycodesList(),
        ]);

        dispatch({
            type: UPDATE_ADMIN_APP_DATA,
            payload: {
                ruleSets: ruleSetsResponse.data,
                dataviews: dataviewListResponse.data,
                hyperfinds: parseHyperfindQueries(hyperfindsResponse.data),
                customDateFormat: customFormatsResponse.data,
                customDataFields: customDataFieldsResponse.data,
                symbolicPeriods: symbolicPeriodsListResponse.data.map(item => ({ id: item.symbolicId, name: item.name })),
                isAdminRuleSetAppDataLoaded: true,
                paycodeList: paycodeList.data.map(item => ({ ...item, value: item.id, label: item.name })).sort((a, b) => a.name.localeCompare(b.name)),
            },
        });
    } catch (error) {
        toastr.error('Loading app data', error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

export const fetchConfigurationSetAppData = () => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });
    try {
        const [
            hyperFindQuery,
            genericNotification,
            breRuleSetList,
            workRuleList,
            settingList,
            segmentTagsList,
            dataviewList,
            payCodeList,
            awardingRulesList,
            timePeriodRatioOptions,
            ruleSets
        ] = await Promise.all([
            api.getHyperFindQueries(),
            api.getGenericNotificationList(),
            api.getBreRuleSets(),
            api.GetWorkRulesList(),
            api.GetSettings(),
            api.GetScheduleTags(),
            api.GetDataviewsList(),
            api.GetPaycodesList(),
            api.GetAwardingRulesList(),
            api.GetTimePeriodRatioOptions(),
            api.getAllRuleSet()
        ]);
        dispatch({
            type: UPDATE_ADMIN_APP_DATA,
            payload: {
                settingList: settingList.data,
                hyperfinds: parseHyperfindQueries(hyperFindQuery.data),
                segmentTagsList: formatResponse(segmentTagsList.data, 'id', true),
                breRuleSetList: formatResponse(breRuleSetList.data, 'id', true),
                workRuleList: formatResponse(workRuleList.data, 'workRuleId', true),
                dataviews: dataviewList.data,
                payCodeList: formatResponse(payCodeList.data, 'id', false),
                awardingRulesList: formatResponse(awardingRulesList.data, 'id', false),
                genericNotificationList: formatResponse(genericNotification.data, 'id', true),
                timePeriodRatioOptions: formatResponse(timePeriodRatioOptions.data, 'id', false),
                isConfigurationSetAppDataLoaded: true,
                breakRuleList: ruleSets.data.filter(x => x.ruleSetType === RuleSetType.BreakRule).map(x => ({id: x.ruleSetId, label: x.name})),
            },
        });
    } catch (error) {
        toastr.error('Loading app data', error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

export const refreshConfigurationSets = () => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });
    try {
        const response = await api.GetSettings();
        if (response.status === 200) {
            dispatch({
                type: UPDATE_ADMIN_APP_DATA,
                payload: {
                    settingList: response.data,
                },
            });
        } else {
            toastr.error('Loading custom tags', 'Failed to load');
        }
    } catch (error) {
        toastr.error('Loading app data', error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

export const displayNotificationCustomTags = () => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    dispatch({ type: LOADING_START, [pendingTask]: begin });
    try {
        const response = await api.getNotificationTagList();

        if (response.status === 200) {
            dispatch({
                type: UPDATE_ADMIN_APP_DATA,
                payload: {
                    notificationCustomTags: response.data,
                },
            });
        } else {
            toastr.error('Loading custom tags', 'Failed to load');
        }
    } catch (error) {
        toastr.error('Loading custom tags', error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};
